<template>
  <layout width="540">
    <template slot="header">
      {{ title }}
    </template>

    <template>
      <validation-observer ref="form">
        <v-form>
          <form-text v-if="!edit">
            The connection will be created in the center of the current map
            position. Its position may be changed from the edit item menu.
          </form-text>
          <form-subheader title="General info" />
          <validation-provider
            v-slot="{ errors }"
            name="Connection name"
            rules="required"
          >
            <v-text-field
              v-model.trim="state.name"
              label="Connection name"
              :error-messages="errors"
            />
          </validation-provider>
          <form-color-selector v-model="state.color" label="Color" />
          <form-subheader title="Position" />
          <v-select
            v-model="state.connectionId"
            :items="connectionList"
            item-text="name"
            item-value="id"
            label="Connection"
          />
          <form-subheader title="Image" />
          <image-uploader
            :src="imageUrl"
            @change="handleChangeImage"
            @clear="handleClearImage"
          />
          <form-subheader title="Description" />
          <v-textarea
            v-model="state.description"
            no-resize
            rows="1"
            auto-grow
          />
        </v-form>
      </validation-observer>
    </template>
    <template slot="footer">
      <v-spacer />
      <v-btn text color="text-primary" @click.stop="$emit('close')">
        Cancel
      </v-btn>
      <v-btn
        text
        color="primary"
        depressed
        :loading="loading"
        @click.stop="submit"
      >
        {{ submitBtnTitle }}
      </v-btn>
    </template>
  </layout>
</template>

<script>
import Layout from '@/components/popup/PopupLayoutDefault';
import { ref, onMounted, computed } from '@vue/composition-api';
import { usePromise } from 'vue-composable';
import { connectionService } from '@/modules/connections/api';
import { imageService, objectPropertyService } from '@/modules/common/api';
import { mediaClient } from '@/provider';
import { required } from 'vee-validate/dist/rules';
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate';
import { useConnections } from '@/modules/connections/compositions/connections';
import { connectionservice } from '@/modules/connections/api';

extend('required', {
  ...required,
  message: 'This field is required'
});

export default {
  name: 'ConnectionEdit',
  components: {
    Layout,
    ValidationObserver,
    ValidationProvider
  },
  props: {
    connectionId: {
      type: String,
      default: ''
    },
    defaultSize: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit, root }) {
    const state = ref({
      schemaId: '',
      name: '',
      description: '',
      imageId: '',
      color: 'default',
      connectionId: ''
    });

    const form = ref(null);
    const connection = ref({});
    const {
      list: connectionList,
      currentConnectionExceptAllItem
    } = useConnections();

    const edit = computed(() => props.connectionId);
    const title = computed(() =>
      edit.value ? 'Edit connection' : 'Add connection'
    );
    const submitBtnTitle = computed(() => (edit.value ? 'Save' : 'Add'));

    const imageLoaded = ref(false);

    const imageUrl = computed(() =>
      mediaClient.getImageUrl(state.value.imageId)
    );

    const handleChangeImage = async e => {
      state.value.imageId = await imageService.upload(e.target.files[0]);
    };

    const handleClearImage = () => {
      state.value.imageId.value = '';
      imageLoaded.value = false;
    };

    const handleSubmit = async () => {
      form.value.validate().then(async success => {
        if (!success) {
          return;
        }

        if (!edit.value) {
          // create new connection
          connection.value = await connectionService.create({
            name: state.value.name,
            description: state.value.description,
            schemaId: state.value.schemaId
          });
          await objectPropertyService.updateValues([
            {
              id: connection.value.positionPoints.id,
              value: props.defaultSize
            }
          ]);
        } else {
          await connectionService.update(props.connectionId, {
            name: state.value.name,
            description: state.value.description
          });
        }
        await objectPropertyService.updateValues([
          {
            id: connection.value.infoImageId.id,
            value: state.value.imageId
          },
          {
            id: connection.value.infoColor.id,
            value: state.value.color
          },
          {
            id: connection.value.positionConnectionId.id,
            value: state.value.connectionId
          }
        ]);
        connectionservice.updateLinkToObject(
          connection.value.id,
          connection.value.positionConnectionId.value,
          state.value.connectionId
        );

        if (!edit.value) {
          root.$router.push({
            name: 'connection_card',
            params: {
              connectionId: connection.value.id
            }
          });
        }

        emit('close');
      });
    };

    const { loading, exec: submit } = usePromise(handleSubmit, true);

    onMounted(async () => {
      if (!edit.value) {
        state.value.schemaId = await connectionService.fetchConnectionSchemaId();
        if (!state.value.schemaId) {
          console.warn(`Can't fetch connection schema`);
        }
        state.value.connectionId = currentConnectionExceptAllItem.value.id;
      } else {
        connection.value = await connectionService.fetch(props.connectionId);

        const {
          infoImageId,
          infoColor,
          name,
          description,
          positionConnectionId
        } = connection.value;

        state.value = {
          ...state.value,
          imageId: infoImageId.value,
          color: infoColor.value,
          connectionId: positionConnectionId.value,
          name,
          description
        };
      }
    });

    return {
      state,
      loading,
      title,
      submitBtnTitle,
      submit,
      imageUrl,
      handleClearImage,
      handleChangeImage,
      edit,
      form,
      connectionList
    };
  }
};
</script>
